import type { SurveyGroupPreviewAnswer, SurveyGroupPreviewQuestion } from 'src/types/survey-group-api.types';
import type { Answer, TrendsData } from 'src/pages/results/endpoints/results.type';
import type { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';

const CHART_COLORS = ['#F9AB00', '#3257FB', '#1e8e3c', '#d93026', '#934DFF', '#F25A18', '#14B8A6', '#64748B'];

export interface GroupTrendsMetric {
  key: string;
  /** Full legend label for combined chart */
  name: string;
  /** Answer text only (for per-survey facet legends) */
  answerLabel: string;
  /** Trends answer image when text labels are missing (image-only options). */
  answerImageUrl?: string;
  color: string;
  extSurveyId: string;
}

/** One chart per survey — answer lines only, easier to read than all surveys on one axis. */
export interface GroupTrendsSurveyFacet {
  extSurveyId: string;
  title: string;
  chartData: TimeSeriesDataPoint[];
  metrics: Array<{ key: string; name: string; color: string; imageUrl?: string }>;
}

export interface GroupTrendsQuestionChartModel {
  questionClusterId: string;
  title: string;
  chartData: TimeSeriesDataPoint[];
  metrics: GroupTrendsMetric[];
  /** Per-survey panels (default view) */
  facets: GroupTrendsSurveyFacet[];
  /** False when freeform or no series could be built */
  hasChart: boolean;
  skipReason?: 'freeform' | 'no_data';
}

function truncate(s: string | null | undefined, max: number): string {
  const t = s ?? '';
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max - 1)}…`;
}

function isLikelyUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s.trim());
}

/**
 * Prefer preview cluster title; then trends image; avoid showing raw answer UUIDs when an image exists.
 */
function answerLegendTextAndImage(
  pa: SurveyGroupPreviewAnswer,
  answer: Answer
): { text: string; imageUrl?: string } {
  const imageUrl = answer.image_url?.trim() || undefined;
  const previewTitle = (pa.default_title ?? '').trim();
  if (previewTitle) {
    return { text: truncate(previewTitle, 36), imageUrl };
  }
  if (imageUrl) {
    return { text: '', imageUrl };
  }
  const raw = (answer.title ?? '').trim();
  if (raw && !isLikelyUuid(raw)) {
    return { text: truncate(raw, 36) };
  }
  return { text: 'Answer' };
}

/**
 * Build one combined time series per clustered question: each line is (survey × answer option),
 * aligned on calendar dates across surveys.
 */
export function buildSurveyGroupTrendsQuestionCharts(
  clusters: SurveyGroupPreviewQuestion[],
  trendsBySurvey: Partial<Record<string, TrendsData>>,
  surveyOrder: Array<{ extSurveyId: string; title: string }>
): GroupTrendsQuestionChartModel[] {
  const out: GroupTrendsQuestionChartModel[] = [];

  for (const cluster of clusters) {
    if (!cluster.answers?.length) {
      out.push({
        questionClusterId: cluster.id,
        title: cluster.default_title ?? '',
        chartData: [],
        metrics: [],
        facets: [],
        hasChart: false,
        skipReason: 'freeform',
      });
      continue;
    }

    const metrics: GroupTrendsMetric[] = [];
    let colorIdx = 0;

    /** For each (survey, cluster answer), collect (key, intervals source). */
    const seriesList: Array<{
      key: string;
      intervals: { interval_start: string; raw_percent: number }[];
    }> = [];

    for (const { extSurveyId, title } of surveyOrder) {
      const td = trendsBySurvey[extSurveyId];
      if (!td?.questions) {
        continue;
      }

      let question: (typeof td.questions)[string] | undefined;
      for (const mqId of cluster.mapped_questions) {
        question = td.questions[mqId];
        if (question) {
          break;
        }
      }
      if (!question?.answers) {
        continue;
      }

      for (const pa of cluster.answers) {
        let answer: (typeof question.answers)[string] | undefined;
        for (const aid of pa.mapped_answers) {
          answer = question.answers[aid];
          if (answer) {
            break;
          }
        }
        if (!answer?.intervals?.length) {
          continue;
        }

        const key = `${extSurveyId}__${pa.id}`;
        const surveyShort = truncate(title || extSurveyId, 28);
        const { text: answerText, imageUrl: answerImageUrl } = answerLegendTextAndImage(pa, answer);
        const answerShort = answerText;
        const combinedName = answerShort
          ? `${surveyShort} — ${answerShort}`
          : `${surveyShort} — ${answerImageUrl ? '\u00A0' : 'Answer'}`;
        metrics.push({
          key,
          name: combinedName,
          answerLabel: answerShort || (answerImageUrl ? '\u00A0' : 'Answer'),
          answerImageUrl,
          extSurveyId,
          color: CHART_COLORS[colorIdx % CHART_COLORS.length],
        });
        colorIdx += 1;
        seriesList.push({
          key,
          intervals: answer.intervals.map((iv) => ({
            interval_start: iv.interval_start,
            raw_percent: iv.raw_percent,
          })),
        });
      }
    }

    if (metrics.length === 0) {
      out.push({
        questionClusterId: cluster.id,
        title: cluster.default_title ?? '',
        chartData: [],
        metrics: [],
        facets: [],
        hasChart: false,
        skipReason: 'no_data',
      });
      continue;
    }

    const allDates = new Set<string>();
    for (const s of seriesList) {
      for (const iv of s.intervals) {
        allDates.add(iv.interval_start.split('T')[0]);
      }
    }
    const sortedDates = [...allDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const chartDataSorted: TimeSeriesDataPoint[] = sortedDates.map((date) => {
      const row: TimeSeriesDataPoint = { date };
      for (const s of seriesList) {
        const iv = s.intervals.find((x) => x.interval_start.split('T')[0] === date);
        if (iv) {
          (row as Record<string, number>)[s.key] = iv.raw_percent * 100;
        }
      }
      return row;
    });

    const metricKeys = metrics.map((m) => m.key);
    metricKeys.forEach((mk) => {
      let last: number | undefined;
      chartDataSorted.forEach((dp) => {
        const v = (dp as Record<string, number | undefined>)[mk];
        if (typeof v === 'number' && !Number.isNaN(v)) {
          last = v;
          return;
        }
        if (last !== undefined) {
          (dp as Record<string, number>)[mk] = last;
        }
      });
      let next: number | undefined;
      [...chartDataSorted].reverse().forEach((dp) => {
        const v = (dp as Record<string, number | undefined>)[mk];
        if (typeof v === 'number' && !Number.isNaN(v)) {
          next = v;
          return;
        }
        if (next !== undefined) {
          (dp as Record<string, number>)[mk] = next;
        }
      });
      chartDataSorted.forEach((dp) => {
        const v = (dp as Record<string, number | undefined>)[mk];
        if (typeof v !== 'number' || Number.isNaN(v)) {
          (dp as Record<string, number>)[mk] = 0;
        }
      });
    });

    const chartData = chartDataSorted.filter((dp) => {
      const hasNonZero = metricKeys.some((mk) => {
        const v = (dp as Record<string, number>)[mk];
        return v !== undefined && v !== null && v !== 0;
      });
      return hasNonZero;
    });

    const facets: GroupTrendsSurveyFacet[] = [];
    const metricsBySurvey = new Map<string, GroupTrendsMetric[]>();
    for (const m of metrics) {
      const list = metricsBySurvey.get(m.extSurveyId) ?? [];
      list.push(m);
      metricsBySurvey.set(m.extSurveyId, list);
    }
    for (const { extSurveyId, title } of surveyOrder) {
      const surveyMetrics = metricsBySurvey.get(extSurveyId);
      if (!surveyMetrics?.length) {
        continue;
      }
      const facetChartData: TimeSeriesDataPoint[] = chartData.map((row) => {
        const r: TimeSeriesDataPoint = { date: row.date };
        for (const sm of surveyMetrics) {
          const v = (row as Record<string, number | undefined>)[sm.key];
          if (typeof v === 'number') {
            (r as Record<string, number>)[sm.key] = v;
          }
        }
        return r;
      });
      facets.push({
        extSurveyId,
        title: truncate(title || extSurveyId, 44),
        chartData: facetChartData,
        metrics: surveyMetrics.map((sm) => ({
          key: sm.key,
          name: sm.answerLabel,
          color: sm.color,
          imageUrl: sm.answerImageUrl,
        })),
      });
    }

    out.push({
      questionClusterId: cluster.id,
      title: cluster.default_title ?? '',
      chartData,
      metrics,
      facets,
      hasChart: chartData.length > 0,
      skipReason: chartData.length === 0 ? 'no_data' : undefined,
    });
  }

  return out;
}
